/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function MCPServer() {
  return (
    <>
      <Meta
        title="Working with Vimeo MCP Server"
        description="Learn how to set up and use Vimeo MCP Server to perform nearly any Vimeo operation or access any kind of data about your Vimeo content through an AI chatbot interface."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Vimeo MCP Server</Path.Current>
          </Path>
        }
        label="Working with Vimeo MCP Server"
        nav={{ page: 'mcp-server' }}
      >
        <Header>Working with Vimeo MCP Server</Header>

        <Paragraph size="2">
          <i>Vimeo MCP Server</i> brings Vimeo's video and platform capabilities directly to your AI
          chatbot. With MCP Server, you can access practically all of Vimeo's video infrastructure
          through natural language prompts in near real time.
        </Paragraph>

        <Paragraph size="2">
          The server is powered by the{' '}
          <Link variant="minimal" href="https://www.anthropic.com/news/model-context-protocol">
            Model Context Protocol
          </Link>
          , an open standard designed by{' '}
          <Link variant="minimal" href="https://www.anthropic.com/">
            Anthropic
          </Link>
          . For complete technical details on the standard, see Anthropic's{' '}
          <Link variant="minimal" href="https://modelcontextprotocol.io/docs/getting-started/intro">
            Model Context Protocol documentation
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          This guide walks you through setting up and using Vimeo MCP Server.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Vimeo MCP Server is currently in public beta. Core functionality is
          available, but we're still developing some of the more advanced features, and all of it is
          subject to change without notice. Also, your experience with MCP Server might vary
          according to your Vimeo membership level. For best results, use our{' '}
          <Link variant="minimal" href="https://vimeo.com/upgrade-plan">
            Enterprise plan
          </Link>
          . All that said, we'd love to hear what you think. Share your thoughts on MCP Server
          through our{' '}
          <Link
            variant="minimal"
            href="https://docs.google.com/forms/d/e/1FAIpQLSdWEcloXbbI9vAH_tqOVLy36N95k8LOdIa6Io2bx_CbgZqy-g/viewform"
          >
            public beta feedback form
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A Vimeo account.</strong> See our{' '}
            <Link variant="minimal" href="https://vimeo.com/upgrade-plan">
              upgrade page
            </Link>{' '}
            to sign up for yours. We recommend the Pro plan or above for Vimeo MCP Server. Our more
            fully featured plans come with higher{' '}
            <Link variant="minimal" href="/guidelines/rate-limiting">
              rate limits
            </Link>{' '}
            for greater flexibility.
          </li>
          <li>
            <strong>A Vimeo video library.</strong> Vimeo MCP Server shines with video management
            and analysis tasks. To make the most of MCP Server, upload multiple videos to your
            account.
          </li>
          <li>
            <strong>Access to Claude.</strong>{' '}
            <Link variant="minimal" href="https://claude.ai/">
              Claude
            </Link>{' '}
            is Anthropic's AI assistant. The setup procedures in this guide assume that you're using
            Claude as your chatbot. Free Claude accounts are available, but we recommend a paid plan
            because of the higher usage limits.
          </li>
        </ul>

        <Header size="2" id="supported-tools-routes">
          Understanding supported tools and routes
        </Header>
        <Paragraph size="2">
          You can access most of the{' '}
          <Link variant="minimal" href="/api/reference">
            Vimeo API
          </Link>{' '}
          through Vimeo MCP Server, except for destructive actions like deleting videos for safety
          purposes.{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          summarizes what you can do through MCP Server and what kinds of data you can access.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description={<span>Supported Vimeo functionality in MCP Server</span>}
          headers={['Supported functionality', 'Examples']}
          rows={[
            [
              <span>Video search and discovery</span>,
              <span>Search videos, get user videos, browse staff picks, search across teams.</span>,
            ],
            [
              <span>Video details and metadata</span>,
              <span>Get video information, transcripts, chapters, comments, and text tracks.</span>,
            ],
            [
              <span>Analytics API</span>,
              <span>
                Get video performance metrics, geographic data, viewer retention insights, and
                engagement tracking.
              </span>,
            ],
            [
              <span>Content organization</span>,
              <span>
                Create and manage showcases, add videos to collections, and organize content by
                theme or project.
              </span>,
            ],
            [
              <span>Video management</span>,
              <span>
                Edit video metadata and privacy settings, add chapters, and manage content ratings.
              </span>,
            ],
            [
              <span>User and team management</span>,
              <span>Get user information, list teams, and search across team accounts.</span>,
            ],
            [
              <span>Engagement monitoring</span>,
              <span>Retrieve comments, track likes and views, and monitor audience feedback.</span>,
            ],
            [
              <span>Transcript and captions</span>,
              <span>Get text tracks and retrieve transcript segments with timestamps.</span>,
            ],
            [
              <span>Vimeo Create</span>,
              <span>
                Create editing sessions, add text and media to videos, and render videos
                programmatically.
              </span>,
            ],
            [
              <span>Viewer retention</span>,
              <span>
                Get detailed retention analytics and analyze dropoff and engagement patterns.
              </span>,
            ],
          ]}
        />

        <StyledNotice>
          <strong>NOTE:</strong> Once you've set up MCP Server, you can ask Claude specific
          questions about your access to Vimeo features and data and get detailed, accurate
          responses.
        </StyledNotice>

        <Header size="2" id="connection-to-mcp-with-claude">
          Configuring your connection to MCP Server with Claude
        </Header>
        <Paragraph size="2">
          Before you can use Vimeo MCP Server with Claude, you must configure your connection to the
          server through the Claude UI. In Claude-speak, this connection is called a{' '}
          <i>custom connector</i>. See the{' '}
          <Link
            variant="minimal"
            href="https://modelcontextprotocol.io/docs/develop/connect-remote-servers"
          >
            Model Context Protocol documentation
          </Link>{' '}
          for step-by-step instructions on configuring your custom connector for Vimeo.
        </Paragraph>

        <Paragraph size="2">
          The URL for the Vimeo MCP Server is{' '}
          <Link variant="minimal" href="https://mcp.vimeo.com/mcp">
            https://mcp.vimeo.com/mcp
          </Link>
          . Enter this URL for the custom connector when Claude prompts you to do so. You can name
          this connector however you like, but we recommend <code>Vimeo</code> for obvious reasons.
        </Paragraph>

        <Paragraph size="2">
          Once you've configured the connection and you're ready to start a new chat in Claude, make
          sure that the connector for Vimeo is enabled in Claude's search and tools menu. This means
          that your chats with Claude can now include Vimeo requests for any supported data or
          feature.
        </Paragraph>

        <Paragraph size="2">
          If your Vimeo connector isn't in this menu, something has gone wrong.{' '}
          <Link
            variant="minimal"
            href="https://modelcontextprotocol.io/docs/develop/connect-remote-servers"
          >
            Read over the docs one more time
          </Link>
          , and try the connection again.
        </Paragraph>

        <Header size="2" id="managing-access">
          Managing access to MCP Server with Claude
        </Header>
        <Paragraph size="2">
          If you've set up a team under your Vimeo account, members of your team also have access to
          Vimeo MCP Server. To manage the Vimeo data and features that your team members have access
          to, go to the settings of your Claude account, and open the settings for your Vimeo
          connector. Adjust the settings however you like.
        </Paragraph>

        <Header size="2" id="using-mcp-server">
          Using MCP Server
        </Header>
        <Paragraph size="2">
          To access Vimeo data or perform Vimeo operations through Vimeo MCP Server, enter your
          requests in Claude's chat interface in the form of plain language prompts (in English for
          now).
        </Paragraph>
        <Paragraph size="2">
          In this section, we break down a common use case, and then we give you several sample
          prompts.
        </Paragraph>

        <Header size="3" id="implementing-analytics-dashboard">
          Implementing a quarterly video analytics dashboard
        </Header>
        <Paragraph size="2">
          Here's how you might implement a quarterly video analytics dashboard in a Claude chat with
          Vimeo MCP Server.
        </Paragraph>
        <Paragraph size="2">The general steps are as follows:</Paragraph>
        <FancyListTableOfContents
          anchorPrefix="implementing-analytics-dashboard-steps"
          items={[
            <span>Get your OKR tracking sheet.</span>,
            <span>Gather video performance data.</span>,
            <span>Cross-reference and analyze the data.</span>,
            <span>Generate an insights document.</span>,
            <span>Generate action items.</span>,
          ]}
        />
        <Paragraph size="2">Keep reading for the specific details.</Paragraph>

        <FancyList anchorPrefix="implementing-analytics-dashboard-steps">
          <FancyListItem>
            <Paragraph size="2">First, get your OKR tracking sheet. Prompt Claude: </Paragraph>
            <code>
              Get my Q3 OKRs spreadsheet about developer platform metrics from Google Drive.
            </code>
            <Paragraph size="2">Claude retrieves the requested spreadsheet.</Paragraph>
          </FancyListItem>

          <FancyListItem>
            <Paragraph size="2">Next, gather video performance data. Prompt Claude: </Paragraph>
            <code>
              Find all videos tagged "developer-platform" or "api-tutorial" from the last quarter,
              and get their analytics.
            </code>
            <Paragraph size="2">Claude finds the requested videos. Then prompt Claude: </Paragraph>
            <code>Show viewer retention rates and geographic distribution for each video.</code>
            <Paragraph size="2">Claude displays this information.</Paragraph>
          </FancyListItem>

          <FancyListItem>
            <Paragraph size="2">
              Now cross-reference and analyze the data. Prompt Claude:{' '}
            </Paragraph>
            <code>
              Compare the video engagement metrics against the target KPIs in my spreadsheet.
            </code>
            <Paragraph size="2">Claude runs the comparison. Then ask Claude:</Paragraph>
            <code>Which content pieces are underperforming against our 80% retention target?</code>
            <Paragraph size="2">Claude returns the underperforming content.</Paragraph>
          </FancyListItem>

          <FancyListItem>
            <Paragraph size="2">
              It's time to create an insights document. Prompt Claude:{' '}
            </Paragraph>
            <code>
              Create a Google Doc report that combines the video performance data with our OKR
              progress.
            </code>
            <Paragraph size="2">Claude creates the report. Then prompt Claude: </Paragraph>
            <code>
              Include recommendations for content that needs re-editing based on dropoff points.
            </code>
            <Paragraph size="2">Claude updates the report accordingly.</Paragraph>
          </FancyListItem>

          <FancyListItem>
            <Paragraph size="2">Finally, generate some action items. Prompt Claude: </Paragraph>
            <code>
              Based on videos with less than 50% completion rates and our Q4 content calendar in
              Drive, what videos should we prioritize updating?
            </code>
            <Paragraph size="2">
              Claude makes the prioritization recommendations. Then prompt Claude:{' '}
            </Paragraph>
            <code>
              Create a spreadsheet for tracking which videos need new chapters, better descriptions,
              or re-recording.
            </code>
            <Paragraph size="2">Claude creates the spreadsheet.</Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="3" id="sample-prompts">
          Exploring sample prompts
        </Header>
        <Paragraph size="2">
          It would be impossible to cover every application of Vimeo MCP Server. But{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>{' '}
          provides a few sample starter prompts for each main feature group.
        </Paragraph>

        <GuideTable
          tableNumber={2}
          description={<span>Sample prompts for MCP Server</span>}
          headers={['Supported functionality', 'Sample prompts']}
          rows={[
            [
              <span>Video search and discovery</span>,
              <span>
                <code>Find all our Q3 2025 all-hands recordings and product demos.</code>
                <br /> <br />
                <code>
                  Show me the latest tech talks from the engineering team about API development.
                </code>
                <br />
                <br />
                <code>
                  Search for any videos mentioning "developer platform" or "API pricing" from this
                  year.
                </code>
              </span>,
            ],
            [
              <span>Video details and metadata</span>,
              <span>
                <code>Get the full details and description for video ID 1089714094.</code>
                <br />
                <br />
                <code>
                  Show me the metadata, duration, and upload date for our latest all-hands
                  presentation.
                </code>
                <br />
                <br />
                <code>What chapters and timestamps are in the Q2 product roadmap video?</code>
              </span>,
            ],
            [
              <span>Analytics API</span>,
              <span>
                <code>
                  What's the total view count and geographic distribution for our API tutorial
                  videos?
                </code>
                <br />
                <br />
                <code>
                  Show me engagement metrics for all developer-focused content from last month
                </code>
                <br />
                <br />
                <code>
                  Which countries are watching our technical documentation videos the most?
                </code>
              </span>,
            ],
            [
              <span>Content organization</span>,
              <span>
                <code>
                  Create a new showcase called "Developer Platform Demos 2025" with the dark theme.
                </code>
                <br />
                <br />
                <code>Add all API tutorial videos to the Developer Resources showcase.</code>
                <br />
                <br />
                <code>List all my showcases and tell me which ones have the most videos.</code>
              </span>,
            ],
            [
              <span>Video management</span>,
              <span>
                <code>
                  Update the privacy setting on our internal alpha demo so that it's
                  password-protected.
                </code>
                <br />
                <br />
                <code>
                  Change the title and description of video 1234567 to include "MCP Connector
                  Alpha."
                </code>
                <br />
                <br />
                <code>
                  Add chapter markers at 5:30 for "API Overview" and at 12:15 for "Pricing
                  Discussion."
                </code>
              </span>,
            ],
            [
              <span>User and team management</span>,
              <span>
                <code>What teams do I have access to in Vimeo?</code>
                <br />
                <br />
                <code>Show me all content from user ID 1234567.</code>
                <br />
                <br />
                <code>List my team memberships and their associated video libraries.</code>
              </span>,
            ],
            [
              <span>Engagement monitoring</span>,
              <span>
                <code>Show me all comments on our latest developer platform presentation.</code>
                <br />
                <br />
                <code>What feedback did we get on the API pricing announcement video?</code>
                <br />
                <br />
                <code>Find any questions or concerns in the comments of our MCP demo.</code>
              </span>,
            ],
            [
              <span>Transcript and captions</span>,
              <span>
                <code>
                  Get the full transcript with timestamps from our latest all-hands meeting.
                </code>
                <br />
                <br />
                <code>What was discussed between minutes 10-15 in the API strategy meeting?</code>
                <br />
                <br />
                <code>
                  Show me when they mentioned "first-time user signups" in the product review.
                </code>
              </span>,
            ],
            [
              <span>Vimeo Create</span>,
              <span>
                <code>
                  Create a new video with title slides for our Q4 developer metrics presentation.
                </code>
                <br />
                <br />
                <code>
                  Generate a video compilation of our API success stories with text overlays.
                </code>
                <br />
                <br />
                <code>
                  Build a demo reel combining clips from our technical tutorials with chapter
                  titles.
                </code>
              </span>,
            ],
            [
              <span>Viewer retention</span>,
              <span>
                <code>Where do people drop off in our 30-minute API onboarding video?</code>
                <br />
                <br />
                <code>
                  Show me retention insights for the developer platform overview presentation.
                </code>
                <br />
                <br />
                <code>
                  What's the average watch percentage for our technical documentation videos?
                </code>
              </span>,
            ],
          ]}
        />

        <Header size="2" id="connection-to-mcp-with-chatgpt">
          Configuring your connection to MCP Server with ChatGPT
        </Header>
        <Paragraph size="2">
          Vimeo MCP Server also supports ChatGPT. Everything works exactly the same as described
          above for Claude — the only difference is the endpoint URL. Use{' '}
          <Link variant="minimal" href="https://mcp.vimeo.com/sse">
            https://mcp.vimeo.com/sse
          </Link>{' '}
          instead of <code>https://mcp.vimeo.com/mcp</code> when configuring your connector in
          ChatGPT. All the same tools, features, and prompts apply.
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          To get an idea of the sheer scope of what you can do with Vimeo MCP Server, take a stroll
          through our{' '}
          <Link variant="minimal" href="/api/reference">
            API Reference
          </Link>
          . These are the same endpoints you're accessing through the server. The only endpoints
          that don't have MCP support are the ones for deleting data; everything else is designed to
          work with the chatbot.
        </Paragraph>
        <Paragraph size="2">
          If you find yourself running into issues with the server that this guide and Anthropic's{' '}
          <Link variant="minimal" href="https://modelcontextprotocol.io/docs/getting-started/intro">
            official documentation
          </Link>{' '}
          haven't been able to help you with, you have but to{' '}
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            contact us
          </Link>{' '}
          to open up a channel of communication with a live, helpful human being.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
